import { apiHttpClient } from '@/http/httpClient';
import type { OrganizationSettings } from '@/models/organization/organization-settings';
import type { FeatureToggle } from '@/constants/feature-toggle';
import type { AssignmentReason } from '@/models/assignment/assignment-reason';
import type { TimeSlotPreset } from '@/models/organization/time-slot-preset';
import type { RateGroupPriceSettings } from '@/models/rate-group/rate-group-price-settings';
import type { ExportResultsRequest } from '@/models/download/export-results-request';
import type { ExportResultsResponse } from '@/models/download/export-results-response';
import type { ExportTemplate, ExportTemplateResponse } from '@/models/export-template/export-template';
import type { BranchMappingItem, BranchMappingListItem, GroupMappingItem, GroupMappingListItem } from '@/models/organization/branch-mapping';

const ENDPOINT_FEATURE_TOGGLES = 'Organization/FeatureToggles';
const ENDPOINT_GET_BRANCH_MAPPINGS = 'BranchMappings/Get';
const ENDPOINT_SAVE_BRANCH_MAPPINGS = 'BranchMappings/Save';
const ENDPOINT_GET_GROUP_MAPPINGS = 'BranchMappings/GetGroups';
const ENDPOINT_SAVE_GROUP_MAPPINGS = 'BranchMappings/SaveGroups';
const ENDPOINT_GET_ORGANIZATION_SETTINGS = 'Organization/GetSettings';
const ENDPOINT_UPDATE_ORGANIZATION_SETTINGS = 'Organization/UpdateSettings';
const ENDPOINT_GET_ORGANIZATION_ACTIVE_TRANSPORT_REASONS = 'Organization/GetActiveTransportReasons';
const ENDPOINT_SAVE_ORGANIZATION_TRANSPORT_REASONS = 'Organization/SaveTransportReasons';
const ENDPOINT_GET_ORGANIZATION_ACTIVE_TIME_SLOT_PRESETS = 'Organization/GetActiveTimeSlotPresets';
const ENDPOINT_SAVE_ORGANIZATION_TIME_SLOT_PRESETS = 'Organization/SaveTimeSlotPresets';
const ENDPOINT_GET_ORGANIZATION_PRICE_SETTINGS = 'Organization/GetPriceSettings';
const ENDPOINT_GET_ORGANIZATION_COUNTRY = 'Organization/GetCountry';
const ENDPOINT_GET_ORGANIZATION_DATE_EXE_SETTINGS = 'Organization/GetCatalogOrganizationDataExeSettings';
const ENDPOINT_UPDATE_ORGANIZATION_DATA_EXE_SETTINGS = 'Organization/UpdateCatalogOrganizationDataExeSettings';
const ENDPOINT_EXPORT_RESULTS = 'Organization/ExportResults';
const ENDPOINT_GET_EXPORT_TEMPLATES_LIST = 'Organization/GetExportTemplatesList';
const ENDPOINT_GET_EXPORT_TEMPLATE = 'Organization/GetExportTemplate';
const ENDPOINT_DELETE_EXPORT_TEMPLATE = 'Organization/DeleteExportTemplate';

export const getFeatureToggles = () => {
  return apiHttpClient()
    .get<FeatureToggle[]>(ENDPOINT_FEATURE_TOGGLES, {})
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

export const getOrganizationSettings = (id: number) => {
  return apiHttpClient()
    .get<OrganizationSettings>(`${ENDPOINT_GET_ORGANIZATION_SETTINGS}/${id}`)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

export const updateOrganizationSettings = (organizationSettings: OrganizationSettings) => {
  return apiHttpClient()
    .put(ENDPOINT_UPDATE_ORGANIZATION_SETTINGS, organizationSettings)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

export const getActiveTransportReasons = () => {
  return apiHttpClient()
    .get(ENDPOINT_GET_ORGANIZATION_ACTIVE_TRANSPORT_REASONS)
    .then((response) => response)
    .catch((error) => error);
};

export const saveTransportReasons = (transportReasons: AssignmentReason[]) => {
  return apiHttpClient()
    .post(ENDPOINT_SAVE_ORGANIZATION_TRANSPORT_REASONS, { transportReasons })
    .then((response) => response)
    .catch((error) => error);
};

export const getActiveTimeSlotPresets = () => {
  return apiHttpClient()
    .get(ENDPOINT_GET_ORGANIZATION_ACTIVE_TIME_SLOT_PRESETS)
    .then((response) => response)
    .catch((error) => error);
};

export const saveTimeSlotPresets = (timeSlotPresets: TimeSlotPreset[]) => {
  return apiHttpClient()
    .post(ENDPOINT_SAVE_ORGANIZATION_TIME_SLOT_PRESETS, { timeSlotPresets })
    .then((response) => response)
    .catch((error) => error);
};

export const getOrganizationPriceSettings = (id: number) => {
  return apiHttpClient()
    .get<RateGroupPriceSettings>(`${ENDPOINT_GET_ORGANIZATION_PRICE_SETTINGS}/${id}`)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

export const getOrganizationCountry = (id: number) => {
  return apiHttpClient()
    .get(`${ENDPOINT_GET_ORGANIZATION_COUNTRY}/${id}`)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

export const getOrganizationDataExeSettings = (id: number) => {
  return apiHttpClient()
    .get(`${ENDPOINT_GET_ORGANIZATION_DATE_EXE_SETTINGS}/${id}`)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

export const updateOrganizationDataExeSettings = (dataExeId: number, dataExeName: string | null) => {
  return apiHttpClient()
    .put(ENDPOINT_UPDATE_ORGANIZATION_DATA_EXE_SETTINGS, { dataExeId, dataExeName })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

export const exportResults = (request: ExportResultsRequest) => {
  return apiHttpClient()
    .post<ExportResultsResponse>(ENDPOINT_EXPORT_RESULTS, request)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

export const getExportTemplatesList = () => {
  return apiHttpClient()
    .get<ExportTemplate[]>(ENDPOINT_GET_EXPORT_TEMPLATES_LIST)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

export const getExportTemplate = (id: number) => {
  return apiHttpClient()
    .get<ExportTemplateResponse>(`${ENDPOINT_GET_EXPORT_TEMPLATE}/${id}`)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

export const getBranchMappings = () => {
  return apiHttpClient()
    .get<BranchMappingListItem[]>(ENDPOINT_GET_BRANCH_MAPPINGS)
    .then((response) => response)
    .catch((error) => error);
};

export const saveBranchMappings = (mappings: BranchMappingItem[]) => {
  return apiHttpClient()
    .put(ENDPOINT_SAVE_BRANCH_MAPPINGS, { mappings })
    .then((response) => response)
    .catch((error) => error);
};

export const getGroupMappings = () => {
  return apiHttpClient()
    .get<GroupMappingListItem[]>(ENDPOINT_GET_GROUP_MAPPINGS)
    .then((response) => response)
    .catch((error) => error);
};

export const saveGroupMappings = (mappings: GroupMappingItem[]) => {
  return apiHttpClient()
    .put(ENDPOINT_SAVE_GROUP_MAPPINGS, { mappings })
    .then((response) => response)
    .catch((error) => error);
};

export const deleteExportTemplate = (id: number) => {
  return apiHttpClient()
    .delete(`${ENDPOINT_DELETE_EXPORT_TEMPLATE}/${id}`)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};
